import {
  action$,
  dispatchAction,
  persistentDerivedStream,
  connect,
  ofType,
} from '@ardoq/rxbeach';
import { topBar as TopBar } from './topBar/topBar';
import { viewPane as ViewPane } from 'presentation/viewPane/viewPane';
import { reloadCurrentSlide } from './slideNavigator/actions';
import { renderMainPresentationView } from './actions';
import { SlideNavigatorComponent as SlideNavigatorComponentBase } from './slideNavigator/SlideNavigatorComponent';
import { SlideDescriptionSidebar } from './slideNavigator/SlideDescriptionSidebar';
import { ProductTour } from './productTours';
import { ModelCssManagerGlobalStyle } from 'utils/modelCssManager/ModelCssManagerGlobalStyle';
import { PresentationChromeGlobalStyle } from './chrome';
import { TooltipManager } from '@ardoq/tooltip';
import { useEffectOnce } from '@ardoq/hooks';
import { StackPageManager } from '@ardoq/stack-page-manager';
import { GraphVisualizationFilters } from '@ardoq/color-helpers';
import styled from 'styled-components';
import { combineLatest, map } from 'rxjs';
import { loadedPresentation$ } from './streams/loadedPresentation$';
import { EnhancedPresentation } from '@ardoq/api-types';
import { Slide } from './slideNavigator/types';
import { presentationSlides$ } from './slideNavigator/presentationSlides$';
import { t, type I18nLocale } from '@ardoq/i18n';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { EmptyState } from 'components/AssetsBrowser2024/EmptyState';
import { DoqType } from '@ardoq/doq';
import { FlexBox } from '@ardoq/layout';
import { PptxExportOverlay } from './pptxExport/PptxExportOverlay';
import { PptxExportToasts } from './pptxExport/PptxExportToasts';

const VisualPaneWrapper = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  min-width: 0;
  overflow: auto;
  z-index: 1;
  .menuContainer {
    display: none;
    flex: 0 0 auto;
  }
  @media print {
    width: 100% !important;
    height: auto !important;
    position: relative !important;
    right: inherit !important;
    border: 0;
    margin: 0;

    .contentPane {
      height: 100% !important;
      height: initial !important;
    }

    .nav {
      min-width: 580px;
      height: initial !important;
      overflow: initial !important;
      border: none;
      display: none;
      li {
        display: none;
      }
      li.active {
        display: inline-block;
        border: none;
        a {
          border: none;
        }
      }
    }
  }
`;

type MainViewProps = {
  presentation: EnhancedPresentation | null;
  slides: Slide[];
  i18nLocale: I18nLocale;
};

const MainView = ({ presentation, slides, i18nLocale }: MainViewProps) => {
  useEffectOnce(() => {
    if (module.hot) {
      module.hot.accept('presentation/viewPane/viewPane', () =>
        dispatchAction(reloadCurrentSlide())
      );
      module.hot.accept('presentation/topBar/topBar', () =>
        dispatchAction(reloadCurrentSlide())
      );
    }
  });

  const isEmptyPresentation = presentation !== null && slides.length === 0;

  return (
    <FlexBox flexDirection="column" height="full" overflow="hidden">
      <ModelCssManagerGlobalStyle />
      <PresentationChromeGlobalStyle />
      <GraphVisualizationFilters />
      <TooltipManager />
      <StackPageManager />

      <TopBar />

      <FlexBox flexGrow={1} minHeight="0">
        <VisualPaneWrapper
          role="region"
          aria-label={t(
            i18nLocale,
            'presentationAdmin:viewer.slideContentAriaLabel',
            'Slide content'
          )}
        >
          {isEmptyPresentation ? (
            <EmptyState
              doqType={DoqType.EMPTY_BOX}
              title={t(
                i18nLocale,
                'presentationAdmin:viewer.emptyState.title',
                'Nothing to play here'
              )}
              text={t(
                i18nLocale,
                'presentationAdmin:viewer.emptyState.text',
                "This presentation doesn't have any slides yet."
              )}
            />
          ) : (
            <ViewPane presentationId={presentation?._id} />
          )}
        </VisualPaneWrapper>

        {!isEmptyPresentation && <SlideDescriptionSidebar />}
      </FlexBox>

      {!isEmptyPresentation && (
        <SlideNavigatorComponentBase
          slides={slides}
          presentationName={presentation?.name ?? ''}
        />
      )}
      <ProductTour />
      <PptxExportOverlay />
      <PptxExportToasts />
    </FlexBox>
  );
};

const toMainViewProps = (
  presentation: EnhancedPresentation | null,
  slides: Slide[],
  i18nLocale: I18nLocale
): MainViewProps => ({
  presentation,
  slides,
  i18nLocale,
});

const rerender$ = persistentDerivedStream(
  'presentation-mainview-rerender',
  action$.pipe(ofType(renderMainPresentationView)),
  renderMainPresentationView()
);

export const mainView = connect(
  MainView,
  combineLatest([
    rerender$,
    loadedPresentation$,
    i18nLocale$,
    presentationSlides$,
  ]).pipe(
    map(([, { presentation }, i18nLocale, slides]) =>
      toMainViewProps(presentation, slides, i18nLocale)
    )
  )
);
